import { Anchor } from "../../app/components/anchor"
import classNames from "classnames"
import { useEffect, useState, FC, HTMLAttributes, Fragment } from "react"
import { useRouter } from "next/router"
import PillButton from "../../components/buttons/PillButton"
import navCategories from "./navigationData.json"
import LogoIcon from "@/components/svgs/Logo"

export type NavigationItemType = {
  name: string
  route: string
  isAppLink?: boolean
  isVisible?: boolean
}
export type NavigationType = {
  isNavHidden?: boolean
  navItems: NavigationItemType[]
  mobileSubNavTitle?: string
  categories: {
    name: string
    id: string
    route: string
    options: NavigationItemType[]
    isRouteAppLink?: boolean
  }[]
}
export interface IPageNav {
  buttonLabel?: string
  route?: string
  externalPath?: string
  navigationData?: NavigationType
  noBlok?: boolean
  host?: Record<string, string>
  hideNavLinks?: boolean
  position?: string
}
const pageNavItems = [
  {
    name: "How it Works",
    route: "/how-it-works",
    isAppLink: false,
  },
  {
    name: "Become a Driver",
    route: "/new-drivers",
    isAppLink: false,
  },
  {
    name: "Log In",
    route: "/login",
    isAppLink: false,
  },
]
type IPageNavProps = IPageNav

export const NavCategoryItem: FC<
  NavigationType & HTMLAttributes<HTMLElement>
> = (props) => <div {...props} />

export const SubNavCategoryItem: FC<
  NavigationItemType & HTMLAttributes<HTMLElement>
> = (props) => <div {...props} />

const joinPaths = (...parts: string[]) => {
  return parts
    .map((part, index) => {
      // For the first part, keep as is if it's a full URL
      if (
        index === 0 &&
        (part.startsWith("http://") || part.startsWith("https://"))
      ) {
        return part.replace(/\/+$/, "")
      }
      // Remove leading and trailing slashes for other parts
      const cleaned = part.replace(/^\/+|\/+$/g, "")
      return cleaned.length > 0 ? `/${cleaned}` : ""
    })
    .filter(Boolean)
    .join("")
}

export default function PageNav({
  navigationData,
  buttonLabel,
  route = "shipments/new",
  externalPath,
  host,
  hideNavLinks,
  position = "fixed",
}: IPageNavProps) {
  const appHost = host?.app || process.env.NEXT_PUBLIC_APP_HOST || ""
  const websiteHost =
    host?.website || process.env.NEXT_PUBLIC_WEBSITE_HOST || ""
  const hostName = `${appHost}/shipments/new`
  const router = useRouter()
  const [scrollPosition, setScrollPosition] = useState(0)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [isCategoryShown, setIsCategoryShown] = useState(false)
  const [activeCategory, setActiveCategory] = useState("")

  const data = {
    mobileSubNavTitle:
      navigationData?.mobileSubNavTitle || "What are you shipping",
    navItems: navigationData?.navItems?.length
      ? [...navigationData.navItems]
      : [...pageNavItems],
    categories: navigationData?.categories?.length
      ? [...navigationData.categories]
      : [...navCategories],
    isNavHidden:
      navigationData?.isNavHidden === false
        ? navigationData?.isNavHidden
        : true,
  }

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY)
    }
    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const handleCTAPath = () => {
    if (externalPath) {
      return router.push(externalPath)
    }

    return router.push(`/${route}`)
  }

  return (
    <>
      <div
        id="navigation"
        className={classNames(
          "bg-white w-full m-auto top-0",
          scrollPosition ? "shadow-sm border-gray-100 border-b" : "",
          position == "fixed" ? "fixed z-[9999]" : "",
        )}
      >
        <div className="flex justify-between items-center py-4 px-4 xl:max-w-[1400px] m-auto">
          <Anchor href="/">
            <LogoIcon />
          </Anchor>
          <ul
            className={classNames(
              "items-center m-0",
              hideNavLinks ? "hidden lg:hidden" : "hidden lg:flex",
            )}
          >
            <li className="flex pr-5">
              <PillButton className="min-w-[263px]" onClick={handleCTAPath}>
                {buttonLabel || "Get Shipping Quotes"}
              </PillButton>
            </li>
            {data?.navItems ? (
              data?.navItems.map((navItem, i) => (
                <Fragment key={`fragment-${navItem.route}`}>
                  <li className="flex px-4 divide-teal-400">
                    <Anchor
                      href={navItem.route}
                      className="hidden lg:block text-lg text-gray14 underline-offset-4 decoration-2"
                    >
                      {navItem.name}
                    </Anchor>
                  </li>
                  {i === 0 && (
                    <li className="flex px-4" key={"list-divider"}>
                      <div className="h-[3.25rem] border-l-2 border-gray15"></div>
                    </li>
                  )}
                </Fragment>
              ))
            ) : (
              <>
                <li className="px-4 divide-teal-400">
                  <Anchor
                    href="/how-it-works"
                    className="hidden lg:block text-lg text-gray14 underline-offset-4 decoration-2"
                  >
                    How it Works
                  </Anchor>
                </li>
                <li className="px-4">
                  <div className="h-[3.25rem] border-l-2 border-gray15"></div>
                </li>
                <li className="px-4">
                  <Anchor
                    href="/new-drivers"
                    className="hidden lg:block text-lg underline-offset-4 decoration-2"
                  >
                    Become a Driver
                  </Anchor>
                </li>
                <li className="pl-4">
                  <Anchor
                    href="/login"
                    className="hidden lg:block text-lg text-gray14 underline-offset-4 decoration-2"
                  >
                    Log In
                  </Anchor>
                </li>
              </>
            )}
          </ul>
          <div
            className={classNames(
              hideNavLinks ? "hidden lg:hidden" : "lg:hidden",
            )}
          >
            <button
              aria-label="click navigation"
              className="block border-0 bg-transparent px-2 text-neutral-500 hover:no-underline hover:shadow-none focus:no-underline focus:shadow-none focus:outline-none focus:ring-0 dark:text-neutral-200 lg:hidden"
              type="button"
              onClick={() => {
                setDropdownOpen(!dropdownOpen)
              }}
            >
              {dropdownOpen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="32"
                  viewBox="0 96 960 960"
                  width="32"
                >
                  <path d="m249 849-42-42 231-231-231-231 42-42 231 231 231-231 42 42-231 231 231 231-42 42-231-231-231 231Z" />
                </svg>
              ) : (
                <svg
                  width="32"
                  height="22"
                  viewBox="0 0 32 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="32" height="2" rx="1" fill="#4D4D4D" />
                  <rect y="10" width="32" height="2" rx="1" fill="#4D4D4D" />
                  <rect y="20" width="32" height="2" rx="1" fill="#4D4D4D" />
                </svg>
              )}
            </button>
          </div>
        </div>
        <div
          className={`${
            dropdownOpen ? "visible" : "hidden"
          } absolute z-[100] top-18 w-full flex-grow basis-[100%] items-center lg:hidden lg:basis-auto bg-white h-screen`}
          id="navbarSupportedContent1"
          data-te-collapse-item
        >
          <ul className="m-0 px-4 py-0">
            <li className="py-3">
              <PillButton onClick={handleCTAPath}>
                {buttonLabel || "Get Shipping Quotes"}
              </PillButton>
            </li>
            {data?.navItems?.map((navItem) => (
              <li className="block py-3" key={navItem.name}>
                <Anchor
                  href={joinPaths(websiteHost, navItem.route)}
                  className="text-lg text-gray14 underline-offset-4 decoration-2"
                >
                  {navItem.name}
                </Anchor>
              </li>
            ))}
          </ul>
        </div>
        <nav
          className={`bg-skyblue2  w-full z-20 border-y flex items-center m-auto border-gray-100 ${
            scrollPosition > 0 ? "hidden" : "animate-fade-in"
          } ${data?.isNavHidden ? "hidden" : ""}`}
        >
          <ul
            id="menu"
            className="hidden lg:flex items-center px-4 m-auto justify-center font-normal text-base font-roboto py-2 text-gray14 prose-a:text-gray14 prose-a:font-normal prose-a:no-underline"
          >
            {data?.categories?.map((category) => (
              <li key={category.id}>
                <div className="mx-auto flex w-full items-center justify-center">
                  <div className="group relative cursor-pointer w-auto md:mr-4 lg:mr-6 xl:mr-12 text-left">
                    <div className="flex items-center justify-between p-0 z-[60]">
                      <Anchor
                        href={joinPaths(
                          category?.isRouteAppLink ? hostName : websiteHost,
                          category.route,
                        )}
                        className="menu-hover text-base md:text-xs xl:text-sm font-medium no-underline"
                      >
                        {category.name}
                      </Anchor>
                    </div>
                    <div className="invisible absolute z-50 flex w-[160px] -left-4  flex-col pt-[0.55rem] text-gray-800 group-hover:visible bg-transparent">
                      <div className="bg-white rounded-br rounded-bl md:px-4 shadow-md">
                        {category?.options?.map((item) => {
                          if (!item.isVisible) {
                            return null
                          }
                          return (
                            <Anchor
                              href={joinPaths(
                                item?.isAppLink ? hostName : websiteHost,
                                item.route,
                              )}
                              key={item.name}
                              className="my-2 block py-1 hover:text-blue7 md:mx-2"
                            >
                              {item.name}
                            </Anchor>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </nav>
        <nav
          className={`font-normal text-base m-0 items-center shadow-md ${
            data?.isNavHidden ? "hidden" : ""
          }`}
        >
          <div
            className={`bg-skyblue2 lg:hidden flex text-base items-center justify-between py-3 px-4 shadow-md ${
              scrollPosition > 0 ? "hidden" : "animate-fade-in"
            }`}
            onClick={() => setIsCategoryShown(!isCategoryShown)}
          >
            <span>{data.mobileSubNavTitle}</span>
            <span>
              <svg
                width="14"
                height="10"
                viewBox="0 0 10 6"
                fill="none"
                className={isCategoryShown ? "rotate-180 mr-4" : "mr-4"}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1L5 5L9 1"
                  stroke="black"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
          {isCategoryShown && (
            <ul
              className={`px-4 m-0 pb-4 ${
                scrollPosition > 0 ? "hidden" : "animate-fade-in"
              }`}
            >
              {data?.categories?.map((category) => (
                <li
                  key={category.id}
                  onClick={() =>
                    activeCategory === category.id
                      ? setActiveCategory("")
                      : setActiveCategory(category.id)
                  }
                >
                  <div className="flex text-base items-center justify-between pt-6 font-medium no-underline">
                    <Anchor
                      href={joinPaths(
                        category?.isRouteAppLink ? hostName : websiteHost,
                        category.route,
                      )}
                      className="text-gray14 no-underline"
                    >
                      {category.name}
                    </Anchor>
                    <span className="hidden">
                      <svg
                        width="14"
                        height="10"
                        viewBox="0 0 10 6"
                        fill="none"
                        className={
                          activeCategory === category.id
                            ? "rotate-18 mr-40"
                            : "mr-4"
                        }
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1L5 5L9 1"
                          stroke="black"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>

                  <div
                    className={`${
                      activeCategory === category.id
                        ? "visible flex"
                        : "invisible hidden"
                    } w-full flex-col md:px-4 text-gray-800 bg-white`}
                  >
                    <div className="bg-white md:mt-[20.8px] rounded-br rounded-bl">
                      {category.options.map((item) => {
                        if (!item.isVisible) {
                          return null
                        }
                        return (
                          <Anchor
                            href={joinPaths(
                              item?.isAppLink ? hostName : websiteHost,
                              item.route,
                            )}
                            key={item.name}
                            className="my-2 block py-1 text-gray14 hover:text-blue7 md:mx-2"
                          >
                            {item.name}
                          </Anchor>
                        )
                      })}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </nav>
      </div>
    </>
  )
}
